import type { ObjectViewModelProps } from './models/BasicObjectViewModel';
import { BasicObjectViewModel } from './models/BasicObjectViewModel';
import { ImageSliceViewModel } from './models/ImageSliceViewModel';
import type {
  ICenterLineViewModel,
  IDefaultMeasurementsProps,
  IImageSliceViewModel,
  IMeasurementViewModel,
  IObjectViewModel,
  ISceneItemDetailsFactory,
  IViewFactory,
  JSONConfig,
  ViewPortType
} from './interfaces';
import { Transparency } from './interfaces';
import { OrientationPresetKind } from './interfaces';
import { ViewPortKind } from './interfaces';
import type {
  GroupInfo,
  MeasurementBoxInfo,
  ObjectBoxInfo,
  TuneControlInfo,
  ViewPortInfo,
  ViewProps
} from './models/viewEntity/view.entity.ts';
import { ViewEntity } from './models/viewEntity/view.entity.ts';
import { ViewPortOrientation } from './models/ViewPortOrientation';
import type { SliceType } from './modules/images/interfaces';
import type { MeasurementViewModelProps } from './models/MeasurementViewModel';
import { MeasurementViewModel } from './models/MeasurementViewModel';
import * as THREE from 'three';
import { ViewPort3DDetails } from './models/ViewPort3DDetails';
import { OrientationPreset } from './models/OrientationPreset';
import type { CenterLineViewModelProps } from './models/CenterLineViewModel';
import { CenterlineViewModel } from './models/CenterLineViewModel';
import { ViewPortTooltipViewModel } from './models/ViewPortTooltipViewModel';

const defaultObjectModelParams = {
  isHighlighted: false,
  isSelected: false,
  isPickable: false,
  isTunable: false,
  isEditingActivated: false,
  isClippable: true,
  attachments: [],
  highlightColor: '#ffdb85',
  selectionColor: '#e5a100'
};

export const defaultMeasurementModelParams: IDefaultMeasurementsProps = {
  isVisible: true,
  isHighlighted: false,
  isSelected: false,
  transparency: Transparency.Opaque,
  isPickable: true,
  isTunable: false,
  isEditingActivated: false,
  isClippable: true,
  isContourVisible: false,
  highlightColor: '#ffdb85',
  selectionColor: '#e5a100',
  color: '#00A3FF',
  zoomFactor: 0.5
};

const defaultFrontOrientation = 'heartFront';

const defaultCameraFittingFactor = 0.95;

export class ViewFactory implements IViewFactory {
  private _config: JSONConfig.Config;

  private _descriptorToObjectMap: Map<string, JSONConfig.Object> = new Map();

  private _descriptorToCLMap: Map<string, JSONConfig.CenterLine> = new Map();

  private _descriptorToOrientationMap: Map<string, JSONConfig.Orientation> = new Map();

  private _descriptorToViewPortMap: Map<string, JSONConfig.ViewPort> = new Map();

  private _descriptorToMeasurementMap: Map<string, JSONConfig.Measurement> = new Map();

  private _detailsFactory: ISceneItemDetailsFactory;

  public constructor(config: JSONConfig.Config, detailsFactory: ISceneItemDetailsFactory) {
    this._config = config;
    this._detailsFactory = detailsFactory;
    this._config.objects.forEach((object) => this._descriptorToObjectMap.set(object.descriptor, object));
    this._config.centerLines.forEach((cl) => this._descriptorToCLMap.set(cl.descriptor, cl));
    this._config.viewPortOrientations.forEach((viewPortOrientation) =>
      this._descriptorToOrientationMap.set(viewPortOrientation.descriptor, viewPortOrientation)
    );
    this._config.viewPorts.forEach((viewPort) => this._descriptorToViewPortMap.set(viewPort.descriptor, viewPort));
    this._config.measurements.forEach((measurement) =>
      this._descriptorToMeasurementMap.set(measurement.descriptor, measurement)
    );
  }

  public createViewFromJSON(viewDetails: JSONConfig.View): ViewEntity {
    const viewObjectEntities = viewDetails.objects.map((viewObject): IObjectViewModel => {
      const caseWideObject = this._descriptorToObjectMap.get(viewObject.descriptor);
      if (!caseWideObject) {
        throw new Error('Unknown object descriptor');
      }
      const viewModelProps: ObjectViewModelProps = {
        descriptor: caseWideObject.descriptor,
        attachments: caseWideObject.attachments ?? defaultObjectModelParams.attachments,
        label: caseWideObject.label,
        details: caseWideObject.details ?? this._detailsFactory.createDetails(caseWideObject.kind),
        phaseId: undefined,
        syncSliceDescriptor: caseWideObject.syncSliceDescriptor ?? undefined,
        color: caseWideObject.color,
        isVertexColoring: caseWideObject.isVertexColoring ?? false,
        highlightColor: caseWideObject.highlightColor ?? defaultObjectModelParams.highlightColor,
        selectionColor: caseWideObject.selectionColor ?? defaultObjectModelParams.selectionColor,
        transparency: viewObject.transparency,
        isVisible: viewObject.isVisible,
        selectedIndex: undefined,
        isContourVisible: viewObject.isContourVisible,
        isHighlighted: defaultObjectModelParams.isHighlighted,
        isSelected: viewObject.isSelected ?? defaultObjectModelParams.isSelected,
        isPickable: viewObject.isPickable ?? defaultObjectModelParams.isPickable,
        isTunable: viewObject.isTunable ?? defaultObjectModelParams.isTunable,
        isBeingEdited: viewObject.isEditingActivated ?? defaultObjectModelParams.isEditingActivated,
        isClippable: viewObject.isClippable ?? defaultObjectModelParams.isClippable,
        isAvailable: true,
        kind: caseWideObject.kind,
        imported: true,
        surgeryExtension: caseWideObject.surgeryExtension,
        imagesMode: caseWideObject.imagesMode,
        imagesOverlayColor: caseWideObject.imagesOverlayColor,
        isFluoroOverlay: caseWideObject.isFluoroOverlay
      };
      return new BasicObjectViewModel(viewModelProps);
    });

    const imageSliceObjectEntities = viewDetails.imageSlices.map((imageSlice): IImageSliceViewModel => {
      const viewModelProps: JSONConfig.ImageSlice = {
        descriptor: imageSlice.descriptor,
        label: imageSlice.label,
        color: imageSlice.color,
        borderColor: imageSlice.borderColor,
        isVisible: imageSlice.isVisible,
        imageSlicerMode: imageSlice.imageSlicerMode
      };

      return new ImageSliceViewModel(viewModelProps);
    });

    const viewPorts = viewDetails.viewPorts;
    const viewPortEntities = viewPorts.map((viewPortDescriptor): ViewPortInfo => {
      const viewPort = this._descriptorToViewPortMap.get(viewPortDescriptor);
      if (!viewPort) {
        throw new Error('Unknown view port descriptor');
      }
      if (viewPort.kind === ViewPortKind.ImageSlice) {
        const details = viewPort.details as JSONConfig.ImageSliceViewPortDetails;
        return {
          descriptor: viewPort.descriptor,
          kind: viewPort.kind,
          details: {
            sliceDescriptor: details.sliceDescriptor as SliceType
          }
        };
      } else if (viewPort.kind === ViewPortKind.ThreeD) {
        const details = viewPort.details as JSONConfig.ThreeDViewPortDetails;
        const orientation = this._descriptorToOrientationMap.get(details.defaultOrientationDescriptor);
        if (!orientation) {
          throw new Error('Unknown orientation descriptor');
        }
        return {
          descriptor: viewPort.descriptor,
          kind: viewPort.kind,
          details: new ViewPort3DDetails(
            new ViewPortOrientation(
              new THREE.Vector3().fromArray(orientation.topVector),
              new THREE.Vector3().fromArray(orientation.directionVector)
            ),
            defaultFrontOrientation
          )
        };
      } else {
        throw new Error('Unknown ViewPort kind');
      }
    });

    const viewMeasurementsEntities = viewDetails.measurements.map(
      (measurement: JSONConfig.ViewMeasurement): IMeasurementViewModel => {
        const caseWideMeasurement = this._descriptorToMeasurementMap.get(measurement.descriptor);
        if (!caseWideMeasurement) {
          throw new Error('Unknown measurement descriptor');
        }

        const viewModelProps: MeasurementViewModelProps = {
          descriptor: caseWideMeasurement.descriptor,
          attachments: caseWideMeasurement.attachments,
          label: caseWideMeasurement.label,
          details: caseWideMeasurement.details ?? this._detailsFactory.createDetails(caseWideMeasurement.kind),
          phaseId: undefined,
          syncSliceDescriptor: caseWideMeasurement.syncSliceDescriptor,
          color: caseWideMeasurement.color,
          isLabelColored: caseWideMeasurement.isLabelColored,
          highlightColor: defaultMeasurementModelParams.highlightColor,
          selectionColor: defaultMeasurementModelParams.selectionColor,
          isVisible: measurement.isVisible ?? defaultMeasurementModelParams.isVisible,
          isHighlighted: defaultMeasurementModelParams.isHighlighted,
          isSelected: measurement.isSelected ?? defaultMeasurementModelParams.isSelected,
          transparency: defaultMeasurementModelParams.transparency,
          isPickable: measurement.isPickable ?? defaultMeasurementModelParams.isPickable,
          isTunable: measurement.isTunable ?? defaultMeasurementModelParams.isTunable,
          isBeingEdited: measurement.isEditingActivated ?? defaultMeasurementModelParams.isEditingActivated,
          isContourVisible: defaultMeasurementModelParams.isContourVisible,
          kind: caseWideMeasurement.kind,
          zoomFactor: measurement.zoomFactor ?? defaultMeasurementModelParams.zoomFactor,
          imagesMode: caseWideMeasurement.imagesMode,
          clDescriptor: caseWideMeasurement.clDescriptor,
          isAvailable: true,
          readyForSliding: false,
          isClippable: measurement.isClippable ?? defaultMeasurementModelParams.isClippable,
          imported: true
        };
        return new MeasurementViewModel(viewModelProps);
      }
    );

    const viewCenterLinesEntities = viewDetails.centerLines.map(
      (cl: JSONConfig.ViewCenterLine): ICenterLineViewModel => {
        const caseWideCL = this._descriptorToCLMap.get(cl.descriptor);
        if (!caseWideCL) {
          throw new Error('Unknown centerline descriptor');
        }

        const viewModelProps: CenterLineViewModelProps = {
          descriptor: caseWideCL.descriptor,
          alongSliceDescriptor: caseWideCL.alongSliceDescriptor,
          color: caseWideCL.color,
          selectionColor: caseWideCL.selectionColor,
          isHighlighted: false,
          isSelected: false,
          isVisible: cl.isVisible ?? true
        };
        return new CenterlineViewModel(viewModelProps);
      }
    );

    const measurementBoxesEntities: Array<MeasurementBoxInfo> = viewDetails.measurementBoxes.map(
      (measurementBox): MeasurementBoxInfo => {
        const descriptors = measurementBox.entries;

        const measurementsEntities = descriptors.map((descriptor): IMeasurementViewModel => {
          const measurementViewModel = viewMeasurementsEntities.find((viewModel) => {
            return viewModel.descriptor === descriptor;
          });
          if (!measurementViewModel) {
            throw new Error('Unknown measurement descriptor in measurement box');
          }
          return measurementViewModel;
        });

        return {
          order: measurementBox.order ?? 0,
          kind: measurementBox.kind,
          group: measurementBox.groupDescriptor,
          label: measurementBox.label,
          measurements: measurementsEntities,
          details: measurementBox.details,
          hasCriteria: measurementBox.hasCriteria
        };
      }
    );

    const groupsEntities: Array<GroupInfo> | undefined = viewDetails.planningEntitiesGroups?.map((cG): GroupInfo => {
      return {
        order: cG.order ?? 0,
        label: cG.label,
        descriptor: cG.descriptor,
        kind: cG.kind,
        isExpanded: cG.isExpanded
      };
    });

    const objectBoxesEntities = viewDetails.objectBoxes.map((objectBox): ObjectBoxInfo => {
      const entries = objectBox.entries;

      const objectsEntities = entries.map(({ descriptor }): IObjectViewModel => {
        const objectViewModel = viewObjectEntities.find((viewModel) => viewModel.descriptor === descriptor);

        if (!objectViewModel) {
          throw new Error('Unknown object descriptor in object box');
        }

        return objectViewModel;
      });

      return {
        order: objectBox.order ?? 0,
        kind: objectBox.kind,
        label: objectBox.label,
        group: objectBox.groupDescriptor,
        objects: objectsEntities,
        details: objectBox.details
      };
    });

    const tuneControlEntities: Array<TuneControlInfo> = viewDetails.tuneControls.map((tuneControl): TuneControlInfo => {
      return {
        order: tuneControl.order ?? 0,
        kind: tuneControl.kind,
        label: tuneControl.label,
        group: tuneControl.groupDescriptor,
        units: tuneControl.units?.value ?? '',
        subUnits: tuneControl.units?.subUnits,
        disclaimerType: tuneControl.disclaimerType,
        descriptor: tuneControl.descriptor,
        locatableDescriptors: tuneControl.locatableDescriptors,
        hasReset: tuneControl.hasReset
      };
    });

    const orientationPresetsEntities = viewDetails.orientationPresets.map((orientationPreset): OrientationPreset => {
      return new OrientationPreset(
        orientationPreset.label,
        orientationPreset.descriptor,
        orientationPreset.kind ?? OrientationPresetKind.Predefined
      );
    });

    const viewPortTooltipsEntities = viewDetails.viewPortsTooltips?.map((config: JSONConfig.IViewPortTooltip) => {
      return new ViewPortTooltipViewModel({
        descriptor: config.descriptor,
        text: config.text,
        viewPort: config.viewPort,
        placement: config.placement,
        isShown: true
      });
    });

    const viewProps: ViewProps = {
      descriptor: viewDetails.descriptor,
      label: viewDetails.label,
      sideLabel: viewDetails.sideLabel,
      imageStackDescriptor: viewDetails.imageStackDescriptor,
      objects: viewObjectEntities,
      imageSlices: imageSliceObjectEntities,
      clippingPlanes: new Map<ViewPortType, THREE.Plane>(),
      imagesRoiConfig: viewDetails.imagesRoiConfig,
      viewOrientationDescriptor: viewDetails.viewOrientationDescriptor,
      cameraOrientationDescriptor:
        viewDetails.cameraSettings?.cameraOrientationDescriptor ?? viewDetails.viewOrientationDescriptor,
      cameraFittingFactor: viewDetails.cameraSettings?.fittingFactor ?? defaultCameraFittingFactor,
      fluoroCurvePlaneDescriptor: viewDetails.fluoroCurvePlaneDescriptor,
      viewPorts: viewPortEntities,
      measurements: viewMeasurementsEntities,
      ghostCursor: undefined,
      syncViewsDescriptor: undefined,
      centerLines: viewCenterLinesEntities,
      activeCenterLineDescriptor: viewDetails.activeCenterLineDescriptor,
      measurementBoxes: measurementBoxesEntities,
      objectBoxes: objectBoxesEntities,
      planningEntitiesGroups: groupsEntities ?? [],
      workflowIndications: viewDetails.workflowIndications,
      tuneControls: tuneControlEntities,
      orientationPresets: orientationPresetsEntities,
      viewOptions: viewDetails.viewOptions,
      visibilityDependencies: viewDetails.visibilityDependencies,
      selectionDependencies: viewDetails.selectionDependencies,
      availabilityDescriptor: viewDetails.availabilityDescriptor,
      approvedStatus: false,
      helpers: viewDetails.helpers ?? [],
      viewPortsTooltips: viewPortTooltipsEntities ?? [],
      collapseImages: viewDetails.collapseImages,
      descriptorsMapping: viewDetails.descriptorsMapping,
      descriptor2ViewOption: viewDetails.descriptor2ViewOption
    };

    return new ViewEntity(viewProps);
  }
}
